import { useDark, useMagicKeys, useToggle, whenever } from '@vueuse/core'
import { defineStore } from 'pinia'
import { watch } from 'vue'

/**
 * Defines a store for managing dark mode state and toggle function.
 * The store updates the 'isDark' property based on the current dark mode state.
 * The 'toggle' function can be invoked to toggle between dark and light mode.
 *
 * @returns {Object} The object containing the dark mode state and toggle function.
 * @function defineStore
 */
const useDarkMode = defineStore('darkmode', () => {
  const isDark = useDark({
    selector: 'html',
    attribute: 'class',
    valueDark: 'dark',
    valueLight: '',
  })
  const toggle = useToggle(isDark)

  /**
   * User can toggle dark/light modes by pressing shift+control+d at any time
   */
  const keys = useMagicKeys()
  whenever(keys.shift_control_D, () => {
    toggle()
  })

  /**
   * For UnoVis, add 'theme-dark' to body when in dark mode
   * See https://unovis.dev/docs/guides/theming/#dark-theme-usage
   */
  watch(
    isDark,
    (_isDark) => {
      if (_isDark) {
        document.body.classList.add('theme-dark')
      } else {
        document.body.classList.remove('theme-dark')
      }
    },
    { immediate: true },
  )

  return {
    isDark,
    toggle,
  }
})

export default useDarkMode
